import { Popover, Select, Space, Spin, Typography } from "antd";
import React, { useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { ExclamationCircleOutlined, LoadingOutlined } from "@ant-design/icons";
import { ReactComponent as DividerIcon } from "../../assets/svg/Divider.svg";

import {
  selectAssigneesAndUserGroups,
  selectProvidersDoctors,
  selectProvidersFacility,
  selectSelectedView,
  selectUser
} from "../../store/selector";
import { searchDiagnosisIcdsAPI, searchDiagnosisNamesAPI } from "../../api/api";
import { filterSort, sortListByOrder, stringSort } from "../../utils/sort";

import { desiredOrderOfStatuses } from "../../constant/sort";
import { useGetSetting } from "../../hooks/getSetting";
import { useDebouncedSearch } from "../../hooks/useDebouncedSearch";
import { formatAssignee } from "../../utils/formaters";
import { selectedViews } from "../../constant/table";
import { LIGHT_BLUE, GRAY_400 } from "../../constant/colors";
import { MultiSelect } from "../customComponent/CustomSelect";
import {
  getDiagnosisNotFoundContent,
  getMultiSelectFilterOptions,
  hasActiveSearchResults
} from "../../utils/multiSelectFilters";
import {
  getRelevantPatientsTableConfigFromLocalStorage,
  OOP_MET_STATUS,
  saveRelevantPatientsTableConfigToLocalStorage
} from "../../constant/relevantPatients";
import {
  DIAGNOSIS_FILTER_ISC_CODE,
  DIAGNOSIS_FILTER_NAME,
  FILTER_PATIENT_ASSIGNEE,
  MainBucketsTrackEvents,
  NO_FACILITY_VALUE,
  UNASSIGNED_VALUE
} from "../../constant/filters";
import { useDashboardBucketsTrackingEvent } from "../../utils/pendo/hooks";
import PatientPayersMultiSelect from "../PatientPayersMultiSelect";

const { Text } = Typography;
const { Option } = Select;

export const RelevantPatientFilters = ({ handleFetchRelevantPatientFilter }) => {
  const { t } = useTranslation();
  const { trackEvent } = useDashboardBucketsTrackingEvent();

  const selectedView = useSelector(selectSelectedView);
  const facilities = useSelector(selectProvidersFacility);
  const user = useSelector(selectUser);
  const assignees = useSelector(selectAssigneesAndUserGroups);
  const doctors = useSelector(selectProvidersDoctors);

  const facilitySearchInputRef = useRef("");
  const statusSearchInputRef = useRef("");
  const assigneesSearchInputRef = useRef("");
  const physicianSearchInputRef = useRef("");

  const diagnosisNameSearch = useDebouncedSearch({
    searchAPI: searchDiagnosisNamesAPI,
    errorMessage: t("errorMessages.diagnosis_names")
  });
  const diagnosisIcdSearch = useDebouncedSearch({
    searchAPI: searchDiagnosisIcdsAPI,
    errorMessage: t("errorMessages.diagnosis_icds")
  });

  const [showPhysician, statuses, facilityBasedViewEnabled] = useGetSetting([
    "doctorProviderMode",
    "statuses",
    "fe.facility-based-view.enabled"
  ]);
  const showNoFacilityOption = !(facilityBasedViewEnabled && !user?.hasAdminDepartment);

  // statuses is "" until the settings load; sortListByOrder returns [] for empty input.
  const filteredStatuses = useMemo(() => sortListByOrder(statuses, desiredOrderOfStatuses, "value"), [statuses]);

  const relevantPatientsTableConfig = getRelevantPatientsTableConfigFromLocalStorage();
  const relevantPatientsFilters = relevantPatientsTableConfig?.filters || {};

  const validAssignees =
    relevantPatientsFilters.assignees?.length &&
    relevantPatientsFilters.assignees
      .map((assignee) =>
        assignees.find((x) => {
          // Note: assignees are stored as array of options(value, text),
          // but When user change the selected assignees from the dropdown, it will be replaced by list of ids (option.value)
          return typeof assignee !== "object" ? x.id === assignee : x.id === assignee.value;
        })
      )
      .filter((assignee) => assignee)
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  const assigneesTextToDisplay =
    (validAssignees &&
      validAssignees.length > 0 &&
      validAssignees
        .map(({ firstName, lastName, name, type }) => formatAssignee({ firstName, lastName, name, type }))
        .join(" | ")) ||
    "";

  const getDiagnosisNotFound = () =>
    getDiagnosisNotFoundContent(diagnosisNameSearch.isFetching, diagnosisNameSearch.searchText, t("not_found"));

  const getDiagnosisCodeNotFound = () =>
    getDiagnosisNotFoundContent(diagnosisIcdSearch.isFetching, diagnosisIcdSearch.searchText, t("not_found"));

  const updateRelevantPatientsTableConfig = (updatedFilters) => {
    const updatedConfig = {
      ...relevantPatientsTableConfig,
      filters: updatedFilters,
      currentPage: 1
    };

    saveRelevantPatientsTableConfigToLocalStorage(updatedConfig);
    return updatedFilters;
  };

  const handleInputMultiSelect = (values, options, filterName) => {
    const updatedFilters = {
      ...relevantPatientsTableConfig.filters,
      [filterName]: options
    };

    const finalFilters = updateRelevantPatientsTableConfig(updatedFilters);
    handleFetchRelevantPatientFilter(finalFilters);
  };

  const handleAssigneeChange = (values, options) => {
    const isUnassigned = values.includes(UNASSIGNED_VALUE);
    const assigneeOptions = options
      .filter((o) => o.value !== UNASSIGNED_VALUE)
      .map((o) =>
        o.value === user?.id
          ? { ...o, children: formatAssignee({ firstName: user.firstName, lastName: user.lastName, type: "user" }) }
          : o
      );

    const prevAssignees = relevantPatientsFilters.assignees || [];
    const wasMe = prevAssignees.some((a) => (typeof a === "object" ? a.value : a) === user?.id);
    const wasUnassigned = relevantPatientsFilters.isUnassigned;
    if (values.includes(user?.id) && !wasMe && !assigneesSearchInputRef.current) {
      trackEvent(MainBucketsTrackEvents.FILTER_ASSIGNEE_ME_OPTION_SELECTED);
    }
    if (isUnassigned && !wasUnassigned) {
      trackEvent(MainBucketsTrackEvents.FILTER_ASSIGNEE_UNASSIGNED_OPTION_SELECTED);
    }

    const updatedFilters = {
      ...relevantPatientsTableConfig.filters,
      isUnassigned,
      assignees: assigneeOptions
    };

    const finalFilters = updateRelevantPatientsTableConfig(updatedFilters);
    handleFetchRelevantPatientFilter(finalFilters);
  };

  const handleFacilityChange = (values, options) => {
    const isNoFacility = values.includes(NO_FACILITY_VALUE);
    const facilityOptions = options.filter((o) => o.value !== NO_FACILITY_VALUE);

    if (isNoFacility && !relevantPatientsFilters.isNoFacility) {
      trackEvent(MainBucketsTrackEvents.FILTER_FACILITY_NOFACILITY_OPTION_SELECTED);
    }

    const updatedFilters = {
      ...relevantPatientsTableConfig.filters,
      isNoFacility,
      facilities: facilityOptions
    };

    const finalFilters = updateRelevantPatientsTableConfig(updatedFilters);
    handleFetchRelevantPatientFilter(finalFilters);
  };

  return (
    selectedView === selectedViews.topOpportunity && (
      <Space size={16} direction="vertical" style={{ width: "100%", padding: "0px 16px" }}>
        <Space size={0} style={{ width: "100%" }} direction="vertical">
          <Text strong style={{ color: LIGHT_BLUE }}>
            {t("tableFilters.status")}
          </Text>
          <MultiSelect
            id="filter_patient_status"
            placeholder={t("all")}
            onChange={(values, options) => handleInputMultiSelect(values, options, "statuses")}
            value={relevantPatientsFilters.statuses}
            tooltipContent={relevantPatientsFilters.statuses || []}
            autoClearSearchValue={false}
            mode="multiple"
            maxTagCount={1}
            maxTagTextLength={relevantPatientsFilters.statuses?.length > 1 ? 13 : 16}
            dropdownMatchSelectWidth={false}
            bordered={false}
            style={{ width: "100%" }}
            clearAll={true}
            allowClear={true}
            selectAll={true}
            onClearAll={() => {
              handleInputMultiSelect([], [], "statuses");
              assigneesSearchInputRef.current = "";
            }}
            filterOption={(input, option) => {
              statusSearchInputRef.current = input.toLowerCase();
              return option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
            }}
            onSelectAll={() => {
              const newFilteredStatuses = filteredStatuses.filter(
                (d) =>
                  d.text?.toLowerCase().includes(statusSearchInputRef.current) &&
                  d.value.toLowerCase() != OOP_MET_STATUS.toLowerCase()
              );
              const values = newFilteredStatuses.map(({ value }) => value);
              const options = newFilteredStatuses.map(({ value, text }, index) => ({
                value: value,
                key: index,
                children: text
              }));

              handleInputMultiSelect(values, options, "statuses");
            }}
          >
            {filteredStatuses?.length &&
              filteredStatuses
                .filter(
                  (status) =>
                    (!status.roleRequirement || status.roleRequirement == user?.role) &&
                    status.value.toLowerCase() != OOP_MET_STATUS.toLowerCase()
                )
                .map((status) => (
                  <Option key={status.value} value={status.value}>
                    {status.text}
                  </Option>
                ))}
          </MultiSelect>
        </Space>

        <Space size={0} style={{ width: "100%" }} direction="vertical">
          <Text strong style={{ color: LIGHT_BLUE }}>
            {t("tableFilters.patient_assignee")}
          </Text>

          <MultiSelect
            id={FILTER_PATIENT_ASSIGNEE}
            tooltipContent={assigneesTextToDisplay}
            onChange={handleAssigneeChange}
            value={[
              ...(relevantPatientsFilters.isUnassigned ? [UNASSIGNED_VALUE] : []),
              ...(!validAssignees ? [] : validAssignees.map(({ id }) => id))
            ]}
            autoClearSearchValue={false}
            showSearch
            mode="multiple"
            maxTagCount={1}
            maxTagTextLength={relevantPatientsFilters.assignees?.length > 1 ? 13 : 16}
            allowClear
            style={{ width: "100%" }}
            placeholder={`${t("all")}`}
            onSearch={(value) => {
              assigneesSearchInputRef.current = value.toLowerCase();
            }}
            onBlur={() => {
              assigneesSearchInputRef.current = "";
            }}
            filterOption={(input, option) => {
              if (option.value === user?.id) {
                return (
                  !input ||
                  formatAssignee({ firstName: user.firstName, lastName: user.lastName, type: "user" })
                    .toLowerCase()
                    .includes(input.toLowerCase())
                );
              }
              if (option.localSortKey === "") return !input;
              return option.children?.toLowerCase?.().indexOf(input.toLowerCase()) >= 0;
            }}
            filterSort={(a, b) => {
              const aPinned = a.localSortKey === "";
              const bPinned = b.localSortKey === "";
              if (aPinned && !bPinned) return -1;
              if (bPinned && !aPinned) return 1;
              if (aPinned && bPinned) return 0;
              const aIsSelected = relevantPatientsFilters.assignees?.indexOf(a.value) > -1;
              const bIsSelected = relevantPatientsFilters.assignees?.indexOf(b.value) > -1;
              if ((aIsSelected && bIsSelected) || (!aIsSelected && !bIsSelected)) {
                return a.children.localeCompare(b.children);
              }
              if (aIsSelected && !bIsSelected) {
                return -1;
              }
              if (!aIsSelected && bIsSelected) {
                return 1;
              }
            }}
            bordered={false}
            onSelectAll={() => {
              const filteredAssignees = assignees.filter((d) =>
                d.displayName?.toLowerCase().includes(assigneesSearchInputRef.current)
              );
              const values = filteredAssignees.map(({ id }) => id);
              const options = assignees.map(({ id, displayName, name }, index) => ({
                value: id,
                key: index,
                children: displayName || name
              }));

              handleInputMultiSelect(values, options, "assignees");
            }}
            onClearAll={() => {
              const updatedFilters = {
                ...relevantPatientsTableConfig.filters,
                isUnassigned: false,
                assignees: []
              };
              const finalFilters = updateRelevantPatientsTableConfig(updatedFilters);
              handleFetchRelevantPatientFilter(finalFilters);
              assigneesSearchInputRef.current = "";
            }}
            selectAll
            clearAll
            optionLabelProp="displayName"
          >
            {user && assignees.some(({ id }) => id === user.id) && (
              <Option
                key="me"
                value={user.id}
                localSortKey=""
                displayName={formatAssignee({ firstName: user.firstName, lastName: user.lastName, type: "user" })}
                id="patient_assignee_filter_me"
              >
                {formatAssignee({ firstName: user.firstName, lastName: user.lastName, type: "user" })}{" "}
                <span style={{ color: GRAY_400 }}>{t("bulk_actions.current_user_suffix")}</span>
              </Option>
            )}
            <Option
              key="unassigned"
              value={UNASSIGNED_VALUE}
              localSortKey=""
              displayName={t("unassigned")}
              id="patient_assignee_filter_unassigned"
            >
              {t("unassigned")}
            </Option>
            <Option
              key="assignee-divider"
              disabled
              localSortKey=""
              style={{ padding: "4px 8px", minHeight: 0, lineHeight: 0, fontSize: 0, cursor: "default" }}
            >
              <DividerIcon style={{ width: "100%" }} />
            </Option>
            {assignees
              .filter(({ id }) => id !== user?.id)
              .map(({ id, firstName, lastName, name, type }) => (
                <Option key={id} value={id} displayName={formatAssignee({ firstName, lastName, name, type })}>
                  {formatAssignee({ firstName, lastName, name, type })}
                </Option>
              ))}
          </MultiSelect>
        </Space>

        <Space style={{ width: "100%" }} direction="vertical">
          <Text strong style={{ color: LIGHT_BLUE }}>
            {t("tableFilters.patientFacility")}
          </Text>
          <MultiSelect
            id="filter_patient_facility"
            tooltipContent={relevantPatientsFilters.facilities || []}
            onChange={handleFacilityChange}
            value={[
              ...(relevantPatientsFilters.isNoFacility ? [NO_FACILITY_VALUE] : []),
              ...(relevantPatientsFilters.facilities || [])
            ]}
            autoClearSearchValue={false}
            showSearch
            mode="multiple"
            maxTagCount={1}
            maxTagTextLength={relevantPatientsFilters.facilities?.length > 1 ? 13 : 16}
            allowClear
            style={{ width: "100%" }}
            placeholder={`${t("all")}`}
            filterOption={(input, option) => {
              facilitySearchInputRef.current = input.toLowerCase();
              if (option.value === NO_FACILITY_VALUE) return !input;
              if (option.disabled) return false;
              return option.children?.toLowerCase?.().indexOf(input.toLowerCase()) >= 0;
            }}
            filterSort={filterSort}
            bordered={false}
            clearAll={true}
            selectAll={true}
            onSelectAll={() => {
              const filteredFacilities = facilities.filter((d) =>
                d.name?.toLowerCase().includes(facilitySearchInputRef.current)
              );
              const values = filteredFacilities.map(({ id }) => id);
              const options = facilities.map(({ id, name }, index) => ({
                value: id,
                key: index,
                children: name
              }));
              handleInputMultiSelect(values, options, "facilities");
            }}
            onClearAll={() => {
              const updatedFilters = {
                ...relevantPatientsTableConfig.filters,
                isNoFacility: false,
                facilities: []
              };
              const finalFilters = updateRelevantPatientsTableConfig(updatedFilters);
              handleFetchRelevantPatientFilter(finalFilters);
              facilitySearchInputRef.current = "";
            }}
          >
            {showNoFacilityOption && (
              <Option key="no-facility" value={NO_FACILITY_VALUE} localSortKey="" id="facility_filter_no_facility">
                {t("no_facility")}
              </Option>
            )}
            {showNoFacilityOption && (
              <Option
                key="facility-divider"
                disabled
                localSortKey=""
                style={{ padding: "4px 8px", minHeight: 0, lineHeight: 0, fontSize: 0, cursor: "default" }}
              >
                <DividerIcon style={{ width: "100%" }} />
              </Option>
            )}
            {facilities &&
              !!facilities.length &&
              _.uniqBy(facilities, (facility) => facility.name?.toLowerCase().trim()).map((facility, index) => (
                <Option key={index} value={facility.id} localSortKey={facility.name}>
                  {facility.name}
                </Option>
              ))}
          </MultiSelect>
        </Space>

        {showPhysician && (
          <Space size={0} style={{ width: "100%" }} direction="vertical">
            <div style={{ display: "flex" }}>
              <Text strong style={{ color: LIGHT_BLUE }}>
                {t("tableFilters.physician")}
              </Text>
              <div style={{ cursor: "pointer", width: "14px", marginLeft: "10px" }}>
                <Popover
                  content={
                    <div
                      style={{
                        width: "290px",
                        margin: "5px 20px",
                        fontWeight: "400",
                        whiteSpace: "pre-wrap",
                        color: "rgba(0,0,0,.87)"
                      }}
                    >
                      <span>
                        {t("physician_popover.part_one")}
                        <span style={{ textDecoration: "underline", margin: "5px" }}>
                          {t("physician_popover.assigned")}
                        </span>
                        {t("physician_popover.part_two")}
                        <span style={{ fontStyle: "italic", margin: "5px" }}>
                          &quot;{t("physician_popover.visit_schedule")}&quot;
                        </span>
                        {t("physician_popover.part_three")}
                      </span>
                    </div>
                  }
                >
                  <ExclamationCircleOutlined style={{ fontSize: "14px", color: GRAY_400 }} />
                </Popover>
              </div>
            </div>

            <MultiSelect
              id="filter_patient_physician"
              onChange={(values, options) => handleInputMultiSelect(values, options, "doctors")}
              onSelectAll={() => {
                const filteredDoctors = doctors.filter((d) =>
                  d.name?.toLowerCase().includes(physicianSearchInputRef.current)
                );
                const newValues = filteredDoctors.map(({ id }) => id);
                const existingValues = relevantPatientsFilters.doctors || [];
                const allValues = _.uniq([...existingValues, ...newValues]);
                const options = doctors.map(({ id, name }, index) => ({
                  value: id,
                  key: index,
                  children: name
                }));
                handleInputMultiSelect(allValues, options, "doctors");
              }}
              clearAll={true}
              onClearAll={() => {
                handleInputMultiSelect([], [], "doctors");
                physicianSearchInputRef.current = "";
              }}
              value={relevantPatientsFilters.doctors}
              tooltipContent={relevantPatientsFilters.doctors || []}
              autoClearSearchValue={false}
              showSearch={true}
              mode="multiple"
              maxTagCount={1}
              maxTagTextLength={relevantPatientsFilters.doctors?.length > 1 ? 13 : 16}
              allowClear={true}
              selectAll={true}
              style={{ width: "100%" }}
              placeholder={`${t("all")}`}
              filterOption={(input, option) => {
                physicianSearchInputRef.current = input.toLowerCase();
                return option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
              }}
              filterSort={filterSort}
              bordered={false}
            >
              {doctors &&
                !!doctors.length &&
                _.uniqBy(doctors, (doctor) => doctor.name?.toLowerCase().trim()).map((doctor, index) => (
                  <Option key={index} value={doctor.id} localSortKey={doctor.name}>
                    {doctor.name}
                  </Option>
                ))}
            </MultiSelect>
          </Space>
        )}

        <Space size={0} style={{ width: "100%" }} direction="vertical">
          <Text strong style={{ color: LIGHT_BLUE }}>
            {t("tableFilters.payer_name")}
          </Text>
          <PatientPayersMultiSelect
            id="payer_filter_type"
            onChange={(values, options) => {
              handleInputMultiSelect(values, options, "payers");
            }}
            value={relevantPatientsFilters.payers?.map(({ value }) => value) || []}
            tooltipContent={Object.values(relevantPatientsFilters.payers || {})}
            placeholder={t("all")}
            bordered={false}
            onClear={() => {
              handleInputMultiSelect([], [], "payers");
            }}
            maxTagCount={1}
            maxTagTextLength={relevantPatientsFilters.payers?.length > 1 ? 8 : 16}
          />
        </Space>

        <Space size={0} style={{ width: "100%" }} direction="vertical">
          <Space direction="horizontal" style={{ display: "flex", width: "100%", justifyContent: "space-between" }}>
            <Text strong style={{ color: LIGHT_BLUE }}>
              {t("tableFilters.diagnosis_name")}
            </Text>
            {hasActiveSearchResults(diagnosisNameSearch.searchText, diagnosisNameSearch.results.length) && (
              <Text style={{ color: LIGHT_BLUE }}>{diagnosisNameSearch.results.length} Results</Text>
            )}
          </Space>

          <MultiSelect
            onChange={(values, options) => handleInputMultiSelect(values, options, "diagnosisNames")}
            onSelect={() => diagnosisNameSearch.setSearchText("")}
            onBlur={() => diagnosisNameSearch.setSearchText("")}
            autoClearSearchValue={false}
            searchValue={diagnosisNameSearch.searchText}
            value={relevantPatientsFilters?.diagnosisNames || []}
            tooltipContent={relevantPatientsFilters.diagnosisNames || []}
            id={DIAGNOSIS_FILTER_NAME}
            placeholder={t("all")}
            allowClear={true}
            showSearch={true}
            loading={diagnosisNameSearch.isFetching}
            onSearch={diagnosisNameSearch.onSearch}
            filterOption={false}
            filterSort={(a, b) => stringSort(a.value, b.value)}
            suffixIcon={
              diagnosisNameSearch.isFetching ? <Spin size={"small"} indicator={<LoadingOutlined spin />} /> : <></>
            }
            notFoundContent={getDiagnosisNotFound()}
            maxTagCount="responsive"
            clearAll={hasActiveSearchResults(diagnosisNameSearch.searchText, diagnosisNameSearch.results.length)}
            selectAll={hasActiveSearchResults(diagnosisNameSearch.searchText, diagnosisNameSearch.results.length)}
            onSelectAll={() => {
              const values = diagnosisNameSearch.results.map(({ value }) => value);
              const options = diagnosisNameSearch.results.map(({ value, text }, index) => ({
                value: value,
                key: index,
                children: text
              }));
              handleInputMultiSelect(values, options, "diagnosisNames");
            }}
            onClearAll={() => {
              handleInputMultiSelect([], [], "diagnosisNames");
              diagnosisNameSearch.setResults([]);
              diagnosisNameSearch.setSearchText("");
            }}
            setDiagnosisSearchResults={diagnosisNameSearch.setResults}
            clearSearchValue={() => diagnosisNameSearch.setSearchText("")}
          >
            {getMultiSelectFilterOptions(
              diagnosisNameSearch.searchText,
              diagnosisNameSearch.results,
              (relevantPatientsFilters?.diagnosisNames || []).map((o) => ({ value: o.value, text: o.children }))
            ).map((d, index) => (
              <Option key={index} value={d.value}>
                {d.text}
              </Option>
            ))}
          </MultiSelect>
        </Space>

        <Space size={0} style={{ width: "100%" }} direction="vertical">
          <Space direction="horizontal" style={{ display: "flex", width: "100%", justifyContent: "space-between" }}>
            <Text strong style={{ color: LIGHT_BLUE }}>
              {t("tableFilters.diagnosis_code")}
            </Text>
            {hasActiveSearchResults(diagnosisIcdSearch.searchText, diagnosisIcdSearch.results.length) && (
              <Text style={{ color: LIGHT_BLUE }}>{diagnosisIcdSearch.results.length} Results </Text>
            )}
          </Space>

          <MultiSelect
            id={DIAGNOSIS_FILTER_ISC_CODE}
            onChange={(values, options) => handleInputMultiSelect(values, options, "diagnosisCodeIDs")}
            onSelect={() => diagnosisIcdSearch.setSearchText("")}
            onBlur={() => diagnosisIcdSearch.setSearchText("")}
            autoClearSearchValue={false}
            searchValue={diagnosisIcdSearch.searchText}
            value={relevantPatientsFilters?.diagnosisCodeIDs || []}
            tooltipContent={relevantPatientsFilters.diagnosisCodeIDs || []}
            placeholder={t("all")}
            allowClear={true}
            showSearch={true}
            loading={diagnosisIcdSearch.isFetching}
            onSearch={diagnosisIcdSearch.onSearch}
            filterOption={false}
            filterSort={(a, b) => stringSort(a.value, b.value)}
            suffixIcon={
              diagnosisIcdSearch.isFetching ? <Spin size={"small"} indicator={<LoadingOutlined spin />} /> : <></>
            }
            notFoundContent={getDiagnosisCodeNotFound()}
            maxTagCount="responsive"
            clearAll={hasActiveSearchResults(diagnosisIcdSearch.searchText, diagnosisIcdSearch.results.length)}
            selectAll={hasActiveSearchResults(diagnosisIcdSearch.searchText, diagnosisIcdSearch.results.length)}
            onSelectAll={() => {
              const values = diagnosisIcdSearch.results.map(({ value }) => value);
              const options = diagnosisIcdSearch.results.map(({ value, text }, index) => ({
                value: value,
                key: index,
                children: text
              }));
              handleInputMultiSelect(values, options, "diagnosisCodeIDs");
            }}
            onClearAll={() => {
              handleInputMultiSelect([], [], "diagnosisCodeIDs");
              diagnosisIcdSearch.setResults([]);
              diagnosisIcdSearch.setSearchText("");
            }}
            setDiagnosisIcdsSearchResults={diagnosisIcdSearch.setResults}
            clearSearchValue={() => diagnosisIcdSearch.setSearchText("")}
          >
            {getMultiSelectFilterOptions(
              diagnosisIcdSearch.searchText,
              diagnosisIcdSearch.results,
              (relevantPatientsFilters?.diagnosisCodeIDs || []).map((o) => ({ value: o.value, text: o.children }))
            ).map((d, index) => (
              <Option key={index} value={d.value}>
                {d.text}
              </Option>
            ))}
          </MultiSelect>
        </Space>
      </Space>
    )
  );
};
